import type { DayTrade } from '@/models/day-trading-session';
import type { DayTradeAdjustementID, DayTradeID } from '@/models/ids';
import type { USDCent } from '@/models/base-types';
import type { DayTradeStockCountMode } from '@/models/enums';

function getTradeOrderPriority(trade: DayTrade, hiddenSet: Set<DayTradeID>): number {
  if (hiddenSet.has(trade.id)) return 4;
  switch (trade.state) {
    case 'GOAL_REACHED': return 1;
    case 'STOPLOSS': return 2;
    case 'STOPPED': return 3;
    default: return 0;
  }
}

export function sortDayTrades(trades: DayTrade[], hiddenSet: Set<DayTradeID>): DayTrade[] {
  return [...trades].sort((a, b) => {
    const priorityDiff = getTradeOrderPriority(a, hiddenSet) - getTradeOrderPriority(b, hiddenSet);
    if (priorityDiff !== 0) return priorityDiff;
    return b.updated.localeCompare(a.updated);
  });
}

export interface AdjustmentCalc {
  entryPrice: USDCent;
  allocated: number;
  shares: number;
  isProcessed: boolean;
  isSkipped: boolean;
  isNew: boolean;
}

export interface DayTradeCalculation {
  perEntry: number;
  adjustments: AdjustmentCalc[];
  totalShares: number;
  totalInvested: number;
  avgPrice: number;
  targetPriceAbsolute: number;
  profit: number;  // USD Float
  profitPct: number;
  targetPriceDelta: number;
  isLong: boolean;
  isGoalReachedOrStopLoss: boolean;
}

interface CalculateOptions {
  trade: DayTrade;
  budget: USDCent;
  mode: 'participant' | 'moderator' | 'summary';
  processedAdjustements: DayTradeAdjustementID[];
  skippedAdjustements: DayTradeAdjustementID[];
  overrideAdjustementPrices: { [key: string]: USDCent };
  overrideAdjustmentStockCount: { [key: string]: number };
  stockCountMode: DayTradeStockCountMode;
}

export function calculateDayTrade(options: CalculateOptions): DayTradeCalculation {
  const { trade, budget, mode, processedAdjustements, skippedAdjustements, overrideAdjustementPrices, overrideAdjustmentStockCount, stockCountMode } = options;

  const perEntry = (budget / 100) * trade.share;
  const isLong = trade.type === 'LONG';

  const nullResult: DayTradeCalculation = {
    perEntry: perEntry,
    adjustments: trade.adjustments.map((adj) => ({
      entryPrice:  adj.entryPrice,
      allocated:   0,
      shares:      0,
      isProcessed: false,
      isSkipped:   false,
      isNew:       false,
    })),
    totalShares:             0,
    totalInvested:           0,
    avgPrice:                0,
    targetPriceAbsolute:     0,
    profit:                  0,
    profitPct:               0,
    targetPriceDelta:        0,
    isLong:                  isLong,
    isGoalReachedOrStopLoss: false,
  };

  // summary: STOPPED trades return zeroed result
  if (mode === 'summary' && trade.state === 'STOPPED') {
    return nullResult;
  }

  let firstShares: number | null = null;

  const adjustments: AdjustmentCalc[] = trade.adjustments.map((adj, index) => {
    let isProcessed: boolean;
    let isSkipped: boolean;

    if (mode === 'participant') {
      isProcessed = processedAdjustements.includes(adj.id);
      isSkipped = skippedAdjustements.includes(adj.id);
    } else {
      // moderator & summary: use executed flag
      isProcessed = adj.executed;
      isSkipped = false;
    }

    const isNew = !isProcessed && !isSkipped;
    // summary: always use original entryPrice (no overrides)
    const effectivePrice = mode === 'summary' ? adj.entryPrice : (overrideAdjustementPrices[adj.id] ?? adj.entryPrice);
    const entryPriceDollars = effectivePrice / 100;
    let shares = entryPriceDollars > 0 ? Math.floor(perEntry / entryPriceDollars) : 0;

    // FIRST mode: use first adjustment's shares for all subsequent
    if (index === 0) {
      firstShares = shares;
    } else if (stockCountMode === 'FIRST' && firstShares !== null) {
      shares = firstShares;
    }

    // Override has highest priority
    if (overrideAdjustmentStockCount[adj.id] !== undefined) {
      shares = overrideAdjustmentStockCount[adj.id];
    }

    return {
      entryPrice: effectivePrice,
      allocated: shares * entryPriceDollars,
      shares,
      isProcessed,
      isSkipped,
      isNew,
    };
  });

  const hasInteracted = adjustments.some(a => a.isProcessed || a.isSkipped);
  const relevantAdj = adjustments.filter((a) => a.isProcessed);

  // summary: if no adjustement executed, return zeroed result
  if (mode === 'summary' && !hasInteracted) {
    return nullResult;
  }

  // participant/moderator: if no interaction yet, use all adjustements
  const summaryAdj = hasInteracted ? relevantAdj : adjustments;

  const totalShares = summaryAdj.reduce((sum, a) => sum + a.shares, 0);
  const totalInvested = summaryAdj.reduce((sum, a) => sum + a.allocated, 0);
  const avgPrice = totalShares > 0 ? totalInvested / totalShares : 0;

  const targetPriceDelta = trade.targetPrice / 100;

  let profit: number;
  if ((trade.state === 'GOAL_REACHED' || trade.state === 'STOPLOSS') && trade.exitPrice !== null) {
    const exitPriceDollars = trade.exitPrice! / 100;
    profit = isLong ? totalShares * (exitPriceDollars - avgPrice) : totalShares * (avgPrice - exitPriceDollars);
  } else if (mode === 'summary') {
    profit = 0;
  } else {
    profit = totalShares * targetPriceDelta;
  }

  const profitPct = totalInvested > 0 ? (profit / totalInvested) * 100 : 0;

  const targetPriceAbsolute = (totalShares === 0) ? 0 : isLong ? avgPrice + targetPriceDelta : avgPrice - targetPriceDelta;

  return {
    perEntry:                perEntry,
    adjustments:             adjustments,
    totalShares:             totalShares,
    totalInvested:           totalInvested,
    avgPrice:                avgPrice,
    targetPriceAbsolute:     targetPriceAbsolute,
    profit:                  profit,
    profitPct:               profitPct,
    targetPriceDelta:        targetPriceDelta,
    isLong:                  isLong,
    isGoalReachedOrStopLoss: (trade.state === 'GOAL_REACHED' || trade.state === 'STOPLOSS'),
  };
}
